<template>
	<div>Checking authentication...</div>
</template>

<script setup lang="ts">
	// This component handles the OAuth2/OIDC PKCE callback.
	// Flow summary:
	// 1) Read the authorization `code` returned from sessionStorage.
	// 2) Exchange them for access and refresh tokens via the token endpoint.
	// 3) Persist tokens, compute and store expiry time, fetch current user, then redirect to profile.
	import { computed, onMounted } from "vue"
	import { useRoute, useRouter } from "vue-router"
	import axios from "axios"
	import qs from "qs"
	import { url } from "../helper"
	import { useI18n } from "vue-i18n"
	import { useCurrentUser } from "../composables/useCurrentUser"
	import { useDeviceManager } from "../stores/deviceManager"
	// Access the current route (for ?code=...) and a router instance (for redirects)
	const route = useRoute()
	const router = useRouter()
	const deviceManager = useDeviceManager()

	const callbackUrl = computed(() => {
		if (deviceManager.operatingSystem === "android" || deviceManager.operatingSystem === "ios") {
			return "valormovies://callback"
		}
		return import.meta.env.VITE_CALLBACK_URL

		// if (import.meta.env.MODE === "development") {
		// 	return "https://localhost:5175/callback"
		// } else if (import.meta.env.MODE === "tizen") {
		// 	return "https://back.valor-movies.e-sepia.gr/cauth/callback"
		// } else if (import.meta.env.MODE === "production") {
		// 	// callbackUrl = "https://valor-movies.e-sepia.gr/callback"
		// 	if (deviceManager.operatingSystem === "android" || deviceManager.operatingSystem === "ios") {
		// 		return "valormovies://callback"
		// 	}
		// 	return "https://valor-movies.e-sepia.gr/callback"
		// }
		// return ""
	})

	onMounted(async () => {
		const { locale } = useI18n()

		// Read the authorization code from the query parameters.
		const rawCode = route.query.code
		const code = typeof rawCode === "string" ? rawCode : Array.isArray(rawCode) ? rawCode[0] : undefined

		// Retrieve the PKCE verifier that was generated during the login request.
		const codeVerifier = sessionStorage.getItem("pkce_code_verifier")

		// OAuth client identifier registered with the authorization server.
		const clientId = "zQTiAtJD02-HEn6fvUTE_I2Y5iKFkA9qJN4vkVDKNbI"

		// The redirect URI must match what is registered with the authorization server.
		const redirectUri = callbackUrl.value

		// Guard: Ensure we have both code and verifier before attempting exchange.
		if (!code) {
			console.warn("OAuth callback: missing authorization code in query params")
			return
		}
		if (!codeVerifier) {
			console.warn("OAuth callback: missing PKCE code_verifier in sessionStorage")
			return
		}

		// Construct the payload for the token endpoint (x-www-form-urlencoded).
		const payload = {
			grant_type: "authorization_code",
			client_id: clientId,
			redirect_uri: redirectUri,
			code,
			code_verifier: codeVerifier,
		}

		try {
			// Exchange the authorization code for tokens.
			const response = await axios.post(`${url}/oauth/token`, qs.stringify(payload), {
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
			})

			// Extract tokens and expiry from the response body.
			const { access_token, refresh_token, expires_in } = response?.data ?? {}

			// Basic validation of required token.
			if (!access_token) {
				throw new Error("Token response missing access_token")
			}

			// Persist the access token for subsequent API calls.
			localStorage.setItem("jwt", access_token)

			// Persist refresh token when supplied by the server.
			if (refresh_token) {
				localStorage.setItem("refresh_token", refresh_token)
			}

			// Store absolute expiry time in ms since epoch for easy comparisons.
			// Fallback: if expires_in is missing, default to 1 hour.
			const ttlSec = typeof expires_in === "number" ? expires_in : 3600
			localStorage.setItem("token_expiry", (Date.now() + ttlSec * 1000).toString())

			// After successful authentication, fetch the user's profile to hydrate the app state.
			const { fetchCurrentUser } = useCurrentUser(locale)
			await fetchCurrentUser()

			// Register this device with the backend
			const deviceId = await deviceManager.getCurrentDeviceId()
			const deviceName = deviceManager.getDeviceName()

			try {
				await deviceManager.registerDevice(deviceId, deviceName)
			} catch (error) {
				console.warn("Device registration failed (non-critical):", error)
				// Don't block login flow if device registration fails
			}

			// Cleanup any temporary data used only for the login redirect flow.
			sessionStorage.removeItem("post_auth_redirect")

			// Navigate to the profile page (or a stored redirect path if you add support later).
			const redirectPath = "/profile"
			router.push(redirectPath)
		} catch (error) {
			// If the token exchange fails, log the error.
			// You may enhance this by showing a user-facing error and/or redirecting to login.
			console.error("Token exchange failed:", error)
			localStorage.removeItem("jwt")
		}
	})
</script>

<style scoped>
	div {
		text-align: center;
		padding: 50px;
		font-size: 18px;
	}
</style>
